import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PhoneCall, Play, Pause, Plus, RefreshCw, X, LogIn, LogOut, AlertTriangle, Loader2, Eye, RotateCw } from "lucide-react";
import { Toaster, toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";
import { voiceApi, rupees } from "@/lib/voiceApi";
import { staffLogin, ADMIN_TOKEN_KEY, apiErr } from "@/lib/authApi";

const STATUS_COLOR = {
  queued: "var(--taupe)", dialing: "var(--red-3)", ringing: "var(--red-3)", in_progress: "var(--red-2)",
  completed: "var(--green)", no_answer: "var(--red-2)", busy: "var(--red-2)", failed: "var(--red)", cancelled: "var(--muted)",
};

export default function Voice() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;
  return <Console onLogout={() => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAuthed(false); }} />;
}

function Console({ onLogout }) {
  const [campaigns, setCampaigns] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([voiceApi.campaigns(), voiceApi.scripts()]);
      setCampaigns(c.campaigns || []);
      setScripts(s.items || []);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  return (
    <AdminShell>
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "var(--panel)", border: "1px solid var(--hairline)", color: "var(--text)", borderRadius: 0 } }} />
      <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Eyebrow>Admin · Voice</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Outbound, at scale.</h1>
            <p className="mt-2" style={{ color: "var(--body)" }}>Bolna-powered voice campaigns. Cost-capped, retriable, recorded.</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="voice-refresh" className="btn-ghost" onClick={load}><RefreshCw size={14} /> Refresh</button>
            <button data-testid="voice-new-btn" className="btn-red" onClick={() => setCreateOpen(true)}><Plus size={14} /> New campaign</button>
            <button data-testid="voice-logout" className="btn-ghost" onClick={onLogout}><LogOut size={14} /> Sign out</button>
          </div>
        </div>

        <CampaignList campaigns={campaigns} onSelect={setSelected} onChanged={load} />
        {selected && <CampaignDetail campaign={selected} onClose={() => setSelected(null)} onChanged={load} />}
        {createOpen && <CreateCampaign scripts={scripts} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      </div>
    </AdminShell>
  );
}

function CampaignList({ campaigns, onSelect, onChanged }) {
  if (!campaigns.length) {
    return <Panel className="mt-8 p-8 text-center"><MonoLabel>No campaigns yet · click "New campaign"</MonoLabel></Panel>;
  }
  return (
    <div className="mt-8 space-y-4">
      {campaigns.map((c) => {
        const a = c.aggregates || {};
        const done = (a.completed || 0);
        const pending = (a.queued || 0) + (a.dialing || 0) + (a.ringing || 0) + (a.in_progress || 0);
        const bad = (a.failed || 0) + (a.no_answer || 0) + (a.busy || 0);
        return (
          <Panel key={c.id} className="p-5" data-testid={`campaign-${c.id}`}>
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-serif text-xl truncate">{c.name}</div>
                  <span className="chip" style={{ color: STATUS_COLOR[c.status] || "var(--taupe)", borderColor: STATUS_COLOR[c.status] || "var(--taupe)" }}>{c.status.toUpperCase()}</span>
                </div>
                <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
                  script · {c.script_key} · target ~{c.target_count_at_create} · est {rupees(c.estimated_cost_paise)}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <Stat k="Total" v={a.total || 0} />
                  <Stat k="Done" v={done} color="var(--green)" />
                  <Stat k="Pending" v={pending} color="var(--red-3)" />
                  <Stat k="Failed" v={bad} color="var(--red)" />
                  <Stat k="Talk" v={`${Math.round((a.secs || 0) / 60)}m`} />
                </div>
                <CostBar consumed={c.consumed_paise || 0} estimated={c.estimated_cost_paise || 0} />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid={`view-${c.id}`} onClick={() => onSelect(c)} className="btn-ghost"><Eye size={14} /> View</button>
                {c.status === "draft" && (
                  <button data-testid={`launch-${c.id}`} onClick={() => launch(c.id, onChanged)} className="btn-red"><Play size={14} /> Launch</button>
                )}
                {c.status === "paused" && (
                  <button data-testid={`resume-${c.id}`} onClick={() => launch(c.id, onChanged)} className="btn-red"><Play size={14} /> Resume</button>
                )}
                {c.status === "running" && (
                  <button data-testid={`pause-${c.id}`} onClick={() => pauseC(c.id, onChanged)} className="btn-ghost"><Pause size={14} /> Pause</button>
                )}
                {bad > 0 && (
                  <button data-testid={`rt-failed-${c.id}`} onClick={() => rtFailed(c.id, onChanged)} className="btn-ghost"><RotateCw size={14} /> Retry {bad}</button>
                )}
              </div>
            </div>
          </Panel>
        );
      })}
    </div>
  );
}

function launch(id, cb) { voiceApi.launch(id).then((r) => { toast.success(`Launched · ${r.enqueued} calls queued`); cb(); }).catch((e) => toast.error(apiErr(e))); }
function pauseC(id, cb) { voiceApi.pause(id).then(() => { toast.success("Paused"); cb(); }).catch((e) => toast.error(apiErr(e))); }
function rtFailed(id, cb) { voiceApi.retriggerFailed(id).then((r) => { toast.success(`Requeued ${r.retriggered} calls`); cb(); }).catch((e) => toast.error(apiErr(e))); }

function Stat({ k, v, color }) {
  return <span className="hairline px-2 py-1"><span className="mono-label" style={{ color: "var(--taupe)" }}>{k}</span> <span className="font-serif" style={{ color: color || "var(--text)" }}>{v}</span></span>;
}

function CostBar({ consumed, estimated }) {
  if (!estimated && !consumed) return null;
  const pct = estimated > 0 ? Math.min(999, Math.round((consumed / estimated) * 100)) : 0;
  const over = pct > 100;
  const overLabel = pct > 100 ? ` · ${pct - 100}% over` : "";
  return (
    <div className="mt-3 max-w-[420px]" data-testid="cost-bar">
      <div className="flex items-baseline justify-between text-xs">
        <span className="mono-label" style={{ color: "var(--taupe)" }}>Consumed · live spend</span>
        <span className="font-serif" style={{ color: over ? "var(--red)" : "var(--text)" }}>
          {rupees(consumed)} <span className="mono-label" style={{ color: "var(--muted)" }}>of {rupees(estimated)}{overLabel}</span>
        </span>
      </div>
      <div className="mt-1 h-1.5 hairline overflow-hidden" style={{ background: "var(--panel-2)" }}>
        <div style={{
          width: `${Math.min(pct, 100)}%`, height: "100%",
          background: over ? "var(--red)" : (pct > 80 ? "var(--red-3)" : "var(--green)"),
          transition: "width 400ms ease",
        }} />
      </div>
    </div>
  );
}

function CampaignDetail({ campaign, onClose, onChanged }) {
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setBusy(true);
    try {
      const r = await voiceApi.calls({ campaign_id: campaign.id, ...(status ? { status } : {}), page_size: 100 });
      setCalls(r.items || []); setTotal(r.total || 0);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [campaign.id, status]); // eslint-disable-line

  const retriggerOne = async (c) => {
    try { await voiceApi.retrigger(c.id, "manual retry from detail view"); toast.success("Requeued"); load(); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-40 bg-black/80 overflow-auto p-4">
      <Panel className="max-w-[1200px] mx-auto p-0 relative">
        <div className="p-5 hairline-b flex items-center gap-4 sticky top-0 z-10" style={{ background: "var(--panel)" }}>
          <div className="flex-1 min-w-0">
            <MonoLabel>Campaign detail · {total} calls</MonoLabel>
            <div className="font-serif text-2xl mt-1 truncate">{campaign.name}</div>
          </div>
          <select data-testid="detail-status-filter" className="input-x !py-1.5 !w-auto text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {["queued","dialing","ringing","in_progress","completed","no_answer","busy","failed","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="detail-close" onClick={onClose} className="p-2 hover:text-[var(--red)]"><X size={16} /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left mono-label border-b border-[var(--hairline)]">
              <th className="py-3 px-4">Member</th><th className="py-3 px-4">Phone</th><th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Duration</th><th className="py-3 px-4">Attempts</th><th className="py-3 px-4">Recording</th><th className="py-3 px-4"></th>
            </tr></thead>
            <tbody>
              {busy && calls.length === 0 && <tr><td colSpan={7} className="py-6 text-center mono-label" style={{ color: "var(--taupe)" }}><Loader2 size={14} className="animate-spin inline mr-2" />Loading…</td></tr>}
              {!busy && calls.length === 0 && <tr><td colSpan={7} className="py-6 text-center mono-label" style={{ color: "var(--taupe)" }}>No calls match this filter</td></tr>}
              {calls.map((c) => (
                <React.Fragment key={c.id}>
                  <tr className="hairline-t" data-testid={`call-row-${c.id}`}>
                    <td className="py-3 px-4">
                      <div className="text-sm truncate max-w-[220px]">{c.registration?.name || "—"}</div>
                      <div className="mono-label" style={{ color: "var(--taupe)" }}>{c.registration?.chapter || c.registration?.registration_type}</div>
                    </td>
                    <td className="py-3 px-4 mono-label">{c.phone}</td>
                    <td className="py-3 px-4">
                      <span className="chip" style={{ color: STATUS_COLOR[c.status], borderColor: STATUS_COLOR[c.status] }}>{c.status.toUpperCase()}</span>
                      {c.failure_reason && <div className="mono-label mt-1 max-w-[220px] truncate" style={{ color: "var(--taupe)" }} title={c.failure_reason}>{c.failure_reason}</div>}
                    </td>
                    <td className="py-3 px-4 mono-label">{c.duration_seconds != null ? `${c.duration_seconds}s` : "—"}</td>
                    <td className="py-3 px-4 mono-label">{c.attempt_count}/{c.max_attempts}</td>
                    <td className="py-3 px-4">
                      {c.signed_recording_url ? <audio data-testid={`audio-${c.id}`} controls preload="none" src={c.signed_recording_url} style={{ maxWidth: 200, height: 32 }} /> : <span className="mono-label" style={{ color: "var(--muted)" }}>—</span>}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {c.transcript_text && <button data-testid={`tx-${c.id}`} onClick={() => setExpanded({ ...expanded, [c.id]: !expanded[c.id] })} className="btn-ghost mr-2 !py-1">Transcript</button>}
                      {!["queued","dialing","ringing","in_progress","completed"].includes(c.status) && (
                        <button data-testid={`retry-${c.id}`} onClick={() => retriggerOne(c)} className="btn-red !py-1"><RotateCw size={12} /> Retry</button>
                      )}
                    </td>
                  </tr>
                  {expanded[c.id] && (
                    <tr>
                      <td colSpan={7} className="px-4 pb-4">
                        <div className="hairline p-4" style={{ background: "var(--panel-2)" }}>
                          <MonoLabel>Transcript</MonoLabel>
                          <pre className="text-xs whitespace-pre-wrap mt-3" style={{ color: "var(--body)" }}>{c.transcript_text}</pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </motion.div>
  );
}

function CreateCampaign({ scripts, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [scriptKey, setScriptKey] = useState(scripts[0]?.key || "");
  const [agentId, setAgentId] = useState("");
  const [tf, setTf] = useState('{"status":"paid"}');
  const [avgSecs, setAvgSecs] = useState(45);
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr(""); setBusy(true); setPreview(null);
    let tfObj;
    try { tfObj = tf.trim() ? JSON.parse(tf) : {}; } catch { setErr("target_filter must be valid JSON"); setBusy(false); return; }
    try {
      const r = await voiceApi.createCampaign({ name, script_key: scriptKey, bolna_agent_id: agentId || undefined, target_filter: tfObj, avg_call_secs: avgSecs, max_attempts: maxAttempts });
      setPreview(r);
    } catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <Panel className="w-full max-w-xl p-6 relative" data-testid="create-camp-modal">
        <button onClick={onClose} className="absolute right-4 top-4 p-1 hover:text-[var(--red)]"><X size={16} /></button>
        <MonoLabel>New voice campaign</MonoLabel>
        <h2 className="font-serif text-2xl mt-2">Configure your call blast</h2>

        <div className="mt-5 space-y-4">
          <Field label="Name"><input data-testid="camp-name" className="input-x" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Day-of reminders" /></Field>
          <Field label="Script">
            <select data-testid="camp-script" className="input-x" value={scriptKey} onChange={(e) => setScriptKey(e.target.value)}>
              <option value="">— pick a script (configure in DevAdmin)</option>
              {scripts.map((s) => <option key={s.id} value={s.key}>{s.key} · {s.name}</option>)}
            </select>
          </Field>
          <Field label="Bolna agent id (blank → integration default)"><input data-testid="camp-agent" className="input-x" value={agentId} onChange={(e) => setAgentId(e.target.value)} placeholder="uuid" /></Field>
          <Field label="Target filter (JSON) — keys: tier_id, status, country, city, chapter, business_category, registration_type">
            <textarea data-testid="camp-filter" rows={2} className="input-x !font-mono !text-xs" value={tf} onChange={(e) => setTf(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Avg call (s)"><input data-testid="camp-secs" type="number" className="input-x" value={avgSecs} onChange={(e) => setAvgSecs(+e.target.value)} /></Field>
            <Field label="Max attempts"><input data-testid="camp-max" type="number" className="input-x" min={1} max={5} value={maxAttempts} onChange={(e) => setMaxAttempts(+e.target.value)} /></Field>
          </div>
        </div>

        {err && <div className="mt-3 text-sm hairline p-3 flex items-center gap-2" style={{ color: "var(--red-2)", borderColor: "var(--red)" }}><AlertTriangle size={14} /> {err}</div>}
        {preview && (
          <div className="mt-4 hairline p-4" style={{ background: "var(--panel-2)", borderColor: "var(--green)" }} data-testid="camp-preview">
            <MonoLabel>Estimate saved as draft</MonoLabel>
            <div className="font-serif text-lg mt-2">{preview.estimate.message}</div>
            <div className="mt-3 flex gap-3">
              <button data-testid="camp-launch-now" className="btn-red" onClick={async () => {
                try { const r = await voiceApi.launch(preview.id); toast.success(`Launched · ${r.enqueued} calls queued`); onCreated(); } catch (e) { toast.error(apiErr(e)); }
              }}><PhoneCall size={14} /> Launch now</button>
              <button className="btn-ghost" onClick={onCreated}>Save draft & close</button>
            </div>
          </div>
        )}

        {!preview && (
          <div className="mt-6 flex gap-3">
            <button data-testid="camp-submit" onClick={submit} disabled={busy || !name || !scriptKey} className="btn-red">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {busy ? "Estimating…" : "Save & see estimate"}
            </button>
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        )}
      </Panel>
    </motion.div>
  );
}

function Field({ label, children }) { return <label className="block"><div className="mono-label mb-2">{label}</div>{children}</label>; }

function LoginGate({ onSuccess }) {
  const [phone, setPhone] = useState(""); const [password, setPassword] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const submit = async () => {
    setErr(""); setBusy(true);
    try { const r = await staffLogin(phone.replace(/\D/g, "").slice(-10), password); localStorage.setItem(ADMIN_TOKEN_KEY, r.token); onSuccess(); }
    catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[440px] mx-auto px-4 sm:px-8 py-16">
        <Eyebrow>Voice · Admin</Eyebrow>
        <h1 className="h-hero mt-3 text-3xl">Staff sign-in</h1>
        <Panel className="p-6 mt-6">
          <MonoLabel>Admin / DevAdmin phone</MonoLabel>
          <input data-testid="voice-login-phone" className="input-x mt-3" placeholder="10-digit phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <MonoLabel className="mt-4 block">Password</MonoLabel>
          <input data-testid="voice-login-password" type="password" className="input-x mt-3" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          {err && <div className="text-sm mt-3" style={{ color: "var(--red-2)" }}>{err}</div>}
          <button data-testid="voice-login-btn" onClick={submit} disabled={busy} className="btn-red mt-5 w-full"><LogIn size={14} /> {busy ? "Signing in…" : "Sign in"}</button>
        </Panel>
      </div>
    </div>
  );
}
